import { Box, Wrap } from '@chakra-ui/react';
import { Plus } from 'iconoir-react';
import { useState, type ReactNode } from 'react';

export type PreferenceChipOption = {
  value: string;
  label: string;
  /** Optional leading element (e.g. a country flag) rendered before the label. */
  leading?: ReactNode;
};

export type PreferenceChipGroupProps = {
  options: PreferenceChipOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** When true, only selected chips show until the add trigger expands the list. */
  collapsible?: boolean;
  /** Label of the expand trigger shown in collapsible mode. */
  addLabel?: string;
};

type ChipProps = {
  label: string;
  active: boolean;
  onClick: () => void;
  leading?: ReactNode;
};

/**
 * Single toggle chip. A real `<button>` with `aria-pressed` so screen readers
 * and the keyboard treat it as a toggle. Active = solid charcoal (same language
 * as the primary buttons) so selection is unmistakable; inactive = transparent
 * fill + muted border.
 */
const Chip = ({ label, active, onClick, leading }: ChipProps) => (
  <Box
    as="button"
    aria-pressed={active}
    onClick={onClick}
    display="inline-flex"
    alignItems="center"
    gap={leading ? 1.5 : 0}
    px={3}
    py={2.5}
    borderRadius="full"
    borderWidth="1px"
    fontSize="sm"
    fontWeight={active ? 'semibold' : 'medium'}
    lineHeight="1.2"
    cursor="pointer"
    transitionProperty="background-color, border-color, color"
    transitionDuration="fast"
    bg={active ? 'fg' : 'transparent'}
    borderColor={active ? 'fg' : 'border'}
    color={active ? 'bg' : 'fg'}
    _hover={active ? { opacity: 0.9 } : { borderColor: 'border.emphasized' }}
  >
    {leading}
    {label}
  </Box>
);

/**
 * Generic multi-select chip group. Clicking a chip toggles its value and emits
 * the next selection array (never mutates in place). In `collapsible` mode only
 * the selected chips render until the add trigger reveals the full option list,
 * which keeps long taxonomies (niches) from dominating the layout.
 */
export const PreferenceChipGroup = ({
  options,
  selected,
  onChange,
  collapsible = false,
  addLabel,
}: PreferenceChipGroupProps) => {
  const [expanded, setExpanded] = useState(false);

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const showAll = !collapsible || expanded;
  const visibleOptions = showAll ? options : options.filter((o) => selected.includes(o.value));

  return (
    <Wrap gap={2}>
      {visibleOptions.map((option) => (
        <Chip
          key={option.value}
          label={option.label}
          active={selected.includes(option.value)}
          onClick={() => toggle(option.value)}
          leading={option.leading}
        />
      ))}

      {collapsible && !expanded && (
        <Box
          as="button"
          onClick={() => setExpanded(true)}
          display="inline-flex"
          alignItems="center"
          gap={1}
          px={3}
          py={2.5}
          borderRadius="full"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="border"
          color="fg.muted"
          fontSize="sm"
          fontWeight="medium"
          lineHeight="1.2"
          cursor="pointer"
          transitionProperty="border-color, color"
          transitionDuration="fast"
          _hover={{ borderColor: 'border.emphasized', color: 'fg' }}
        >
          <Plus width={14} height={14} aria-hidden="true" />
          {addLabel}
        </Box>
      )}
    </Wrap>
  );
};

export default PreferenceChipGroup;
